import type { Order, Address } from '@/domain/entities/order.entity';
import {
  ACTIVE_PAYMENT_STATUSES,
  ACTIVE_SHIPPING_STATUSES,
  PAST_PAYMENT_STATUSES,
  DELIVERED_SHIPPING_STATUSES,
  type StatusBadgeInfo,
} from './constants';

// --- Order section classification ---

export type OrderSection = 'active' | 'past' | 'none';

export function getOrderSection(order: Order): OrderSection {
  const pay = order.payment_status.value.toLowerCase();
  const ship = order.shipping_status.value.toLowerCase();

  // Terminal payment states are always "past"
  if (PAST_PAYMENT_STATUSES.has(pay)) return 'past';

  // Paid + delivered = past (completed)
  if (pay === 'paid' && DELIVERED_SHIPPING_STATUSES.has(ship)) return 'past';

  // Active payment + non-delivered shipping = active
  if (ACTIVE_PAYMENT_STATUSES.has(pay) && ACTIVE_SHIPPING_STATUSES.has(ship)) return 'active';

  // Paid but still shipping = active
  if (pay === 'paid' && !DELIVERED_SHIPPING_STATUSES.has(ship)) return 'active';

  return 'none';
}

export function separateOrders(orders: Order[]): { active: Order[]; past: Order[] } {
  const active: Order[] = [];
  const past: Order[] = [];

  for (const order of orders) {
    const section = getOrderSection(order);
    if (section === 'active') active.push(order);
    else if (section === 'past') past.push(order);
  }

  return { active, past };
}

// --- Progress stepper ---

export function getCompletedStepIndex(order: Order): number {
  const pay = order.payment_status.value.toLowerCase();
  const ship = order.shipping_status.value.toLowerCase();

  if (ship === 'fulfilled' || ship === 'delivered') return 4;
  if (ship === 'partially_fulfilled') return 3;
  if (ship === 'placed' || ship === 'shipped') return 2;
  if (['authorized', 'paid', 'partially_paid'].includes(pay)) return 1;
  return 0;
}

// --- Status badge ---

export function getStatusBadgeInfo(order: Order): StatusBadgeInfo {
  const pay = order.payment_status.value.toLowerCase();
  const ship = order.shipping_status.value.toLowerCase();

  if (pay === 'declined') return { label: 'Cancelled', variant: 'error' };
  if (pay === 'refunded') return { label: 'Refunded', variant: 'info' };
  if (pay === 'partially_refunded') return { label: 'Partially Refunded', variant: 'info' };

  if (pay === 'paid' && (ship === 'fulfilled' || ship === 'delivered')) return { label: 'Delivered', variant: 'success' };
  if (ship === 'partially_fulfilled') return { label: 'In Transit', variant: 'success' };
  if (ship === 'placed' || ship === 'shipped') return { label: 'Shipped', variant: 'success' };

  if (pay === 'authorized' || pay === 'paid') return { label: 'Processing', variant: 'warning' };
  if (pay === 'pending') return { label: 'Pending', variant: 'warning' };

  return { label: 'Processing', variant: 'warning' };
}

// --- Formatting helpers ---

export function formatOrderDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    let suffix = 'th';
    if (day % 10 === 1 && day !== 11) suffix = 'st';
    else if (day % 10 === 2 && day !== 12) suffix = 'nd';
    else if (day % 10 === 3 && day !== 13) suffix = 'rd';
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${month} ${day}${suffix}, ${year}`;
  } catch {
    return dateString;
  }
}

export function formatCurrency(
  amount: number,
  locale: string = 'en-US',
  currencyCode: string = 'USD',
  symbolOverride?: string,
): string {
  const dollars = amount / 100;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });

  if (!symbolOverride) return formatter.format(dollars);

  return formatter
    .formatToParts(dollars)
    .map((part) => (part.type === 'currency' ? symbolOverride : part.value))
    .join('');
}

export function formatAddress(address: Address): string {
  return [address.address1, address.address2, address.city, address.state, address.zip, address.country]
    .filter(Boolean)
    .join(', ');
}

export function formatFullAddress(address: Address): { lines: string[] } {
  const lines: string[] = [];
  if (address.address1) lines.push(address.address1);
  if (address.address2) lines.push(address.address2);
  if (address.city || address.zip) lines.push([address.city, address.zip].filter(Boolean).join(', '));
  if (address.state || address.country) lines.push([address.state, address.country].filter(Boolean).join(', '));
  if (address.phone) lines.push(address.phone);
  return { lines };
}

export function getOrderTotalQuantity(order: Order): number {
  return order.line_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) ?? 0;
}

export function getOrderImage(order: Order): string {
  if (order.line_items?.length > 0 && order.line_items[0].image_url) {
    return order.line_items[0].image_url;
  }
  return 'https://placehold.co/80x100/f5f5f5/999?text=No+Image';
}

// --- Modify-order helpers ---

export function canModifyOrder(order: Order): boolean {
  const pay = order.payment_status.value.toLowerCase();
  const ship = order.shipping_status.value.toLowerCase();
  return (
    (pay === 'authorized' || pay === 'paid') &&
    (ship === 'unfulfilled' || ship === 'placed')
  );
}

export function isOrderActive(order: Order): boolean {
  return getOrderSection(order) === 'active';
}

export function isOrderDelivered(order: Order): boolean {
  const ship = order.shipping_status.value.toLowerCase();
  return ship === 'fulfilled' || ship === 'delivered';
}

export function isOrderShipped(order: Order): boolean {
  const ship = order.shipping_status.value.toLowerCase();
  return ['placed', 'shipped', 'partially_fulfilled', 'fulfilled', 'delivered'].includes(ship);
}
